// src/components/POS/payment/OrderSummaryModal.js
import React from "react";
import { formatPrice } from "../../utils/FormatPrice";
import { getPaymentMethodLabel, isHitpayEwalletMethod } from "../../../../constant/paymentMethod";
import ProductImageFallback from "../../../CommonComponents/ProductImageFallback";

function OrderItemImage({ item }) {
    const [imageError, setImageError] = React.useState(false);
    const imageUrl = item.image || item.image_url || item.imageUrl || null;

    return (
        <div className="h-11 w-11 shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-100 shadow-sm">
            {imageUrl && !imageError ? (
                <img
                    src={imageUrl}
                    alt={item.name}
                    className="h-full w-full object-cover"
                    loading="lazy"
                    onError={() => setImageError(true)}
                />
            ) : (
                <ProductImageFallback className="h-full w-full" aria-label="No product image" />
            )}
        </div>
    );
}

export default function OrderSummaryModal({ isOpen, onClose, order, onConfirm, confirming = false }) {
    if (!isOpen || !order) return null;

    const displayOrderNumber = order.order_number || `TMP-${order.id || Date.now()}`;
    const isSavedOrder = !!order.order_number;

    const paymentLabel = order.paymentMethod
        ? getPaymentMethodLabel(order.paymentMethod)
        : "Not selected";
    const isEwalletPayment = isHitpayEwalletMethod(order.paymentMethod);
    const isManualOnlinePayment = order.payment_collection_mode === "MANUAL_ONLINE";

    const change = (order?.paymentMethod === "cash" && order?.cashReceived)
        ? order.cashReceived - order.total
        : 0;
    const totalQty = order.cart?.reduce((sum, item) => sum + Number(item.qty || 0), 0) || 0;
    const uniqueItemCount = new Set(
        (order.cart || []).map((item) => String(item.name || item.product_id || item.id || "").trim().toLowerCase()).filter(Boolean)
    ).size;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-3 py-4 backdrop-blur-sm"
            onClick={() => {
                if (!confirming) onClose();
            }}
        >
            <div
                className="flex max-h-[86dvh] w-full max-w-4xl flex-col overflow-y-auto rounded-[28px] border border-white/70 bg-white shadow-[0_24px_80px_rgba(15,23,42,0.28)] md:flex-row md:overflow-hidden"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex w-full shrink-0 flex-col bg-slate-50 md:min-h-0 md:w-[64%] md:shrink md:flex-1">
                    <div className="shrink-0 border-b border-slate-200 bg-white px-5 py-4">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                            Review Order
                        </p>
                        <div className="mt-1 flex items-start justify-between gap-3">
                            <div>
                                <h3 className="text-xl font-bold text-slate-950">
                                    {isSavedOrder ? `Order #${displayOrderNumber}` : "Order Summary"}
                                </h3>
                                <p className="mt-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                                    {uniqueItemCount} unique {uniqueItemCount === 1 ? "item" : "items"} | {totalQty} total qty
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="max-h-[34dvh] overflow-y-auto px-5 py-3 [scrollbar-gutter:stable] md:min-h-0 md:max-h-none md:flex-1">
                        {order.cart?.length > 0 ? (
                            order.cart.map((item, idx) => (
                                <div
                                    key={item.product_id ?? item.id ?? idx}
                                    className="flex items-center gap-3 border-b border-dashed border-slate-200 py-3 last:border-none"
                                >
                                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white text-[11px] font-bold text-slate-500 shadow-sm">
                                        {idx + 1}
                                    </span>
                                    <OrderItemImage item={item} />
                                    <span className="flex min-w-0 flex-1 items-center gap-3">
                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate text-sm font-semibold text-slate-900">{item.name}</span>
                                            <span className="block text-xs font-medium text-slate-500">Qty {item.qty}</span>
                                        </span>
                                        <span className="shrink-0 rounded-full bg-white px-3 py-1 text-sm font-bold text-slate-900 shadow-sm">
                                            {formatPrice(item.price * item.qty)}
                                        </span>
                                    </span>
                                </div>
                            ))
                        ) : (
                            <p className="text-gray-500">No items in this order.</p>
                        )}
                    </div>
                </div>

                <div className="flex w-full shrink-0 flex-col justify-between border-t border-slate-200 bg-white p-5 md:w-[36%] md:shrink-0 md:border-l md:border-t-0">
                    <div className="space-y-4">
                        <div
                            className="rounded-2xl p-4 text-white shadow-[0_16px_38px_rgba(15,23,42,0.14)]"
                            style={{
                                background: "linear-gradient(135deg, var(--color-primary), color-mix(in srgb, var(--color-primary) 78%, black))",
                            }}
                        >
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: "rgba(255,255,255,0.72)" }}>Amount Due</p>
                            <p className="mt-1 text-3xl font-bold leading-none" style={{ color: "#fff" }}>{formatPrice(order.total)}</p>
                            <div className="mt-4 space-y-1 rounded-xl bg-white/12 px-3 py-2 text-xs">
                                <div className="flex justify-between">
                                    <span style={{ color: "rgba(255,255,255,0.78)" }}>Subtotal</span>
                                    <span className="font-semibold" style={{ color: "#fff" }}>{formatPrice(order.subtotal)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span style={{ color: "rgba(255,255,255,0.78)" }}>Discount</span>
                                    <span className="font-semibold" style={{ color: "#fff" }}>{formatPrice(order.discount || 0)}</span>
                                </div>
                            </div>
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-3">
                            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Payment Details</p>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between gap-3">
                                    <span className="text-slate-500">Seat Number</span>
                                    <span className="font-semibold text-slate-900">{order.seat_number || "N/A"}</span>
                                </div>
                                <div className="flex justify-between gap-3">
                                    <span className="text-slate-500">Payment Method</span>
                                    <span className="font-semibold text-slate-900">{paymentLabel}</span>
                                </div>
                                {isManualOnlinePayment && (
                                    <>
                                        <div className="flex justify-between gap-3">
                                            <span className="text-slate-500">Collection</span>
                                            <span className="font-semibold text-slate-900">Manual</span>
                                        </div>
                                        <div className="flex justify-between gap-3">
                                            <span className="text-slate-500">Reference</span>
                                            <span className="font-semibold text-slate-900">•••• {order.payment_reference_last4}</span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {order.paymentMethod === "cash" && order.cashReceived && (
                            <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-500">Cash Received</span>
                                    <span className="font-bold text-slate-900">{formatPrice(order.cashReceived)}</span>
                                </div>
                                <div className="mt-2 flex justify-between rounded-xl bg-slate-50 px-3 py-2 text-sm">
                                    <span className="font-medium text-slate-500">Change</span>
                                    <span className="font-bold text-slate-900">{formatPrice(change)}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="mt-5 grid grid-cols-2 gap-3">
                        <button
                            onClick={onClose}
                            disabled={confirming}
                            className="h-12 rounded-xl bg-slate-100 px-4 text-sm font-semibold text-slate-600 transition hover:bg-slate-200 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={onConfirm}
                            disabled={confirming}
                            className="h-12 rounded-xl px-4 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 focus:outline-none disabled:cursor-wait disabled:opacity-75"
                            style={{ background: 'var(--color-primary)' }}
                        >
                            {confirming
                                ? (isEwalletPayment && !isManualOnlinePayment ? `Opening ${paymentLabel}...` : "Confirming...")
                                : (isEwalletPayment && !isManualOnlinePayment ? `Continue to ${paymentLabel}` : "Confirm Order")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
